import SearchParams from "./searchparams";
import {objectWithAccessors } from "../utils/index";
import TimeoutView from "../context/payment/common/timeout";
import { SET_SESSION_EXPIRY, SET_SESSION_EXPIRED } from "../context/helpers/actions";


// Per-payment-method session duration overrides, in minutes.
// These take precedence over the default/merchant duration and are NOT
// subject to the 15-minute cap (e.g. OPay requires a 30-minute window).
const METHOD_SESSION_DURATION = Object.freeze({
    opay: 30
});

// Minimum time left after a method switch, in milliseconds. The session window
// is anchored to a single absolute start (see below), so switching late to a
// shorter-window method could otherwise expire the user instantly while they
// are actively trying to pay. This grace floor prevents that without letting
// the session start over from scratch on every switch.
const SWITCH_GRACE_MS = 2 * 60000;

// Handle to the single in-flight timeout session. Every getTimeout() call owns
// its own timer/subscription/flags via closure state; this only lets a new
// initialization dispose the previous session. A disposed session's timers and
// store callback are inert (they check their own `disposed` flag), so stale
// chains from hot reloads or re-inits cannot fire or leak.
let activeSession = null;

function getTimeout(data, store) {
   if (!data || !store) return;

   // Tear down any previously-armed session before starting a new one.
   if (activeSession) {
       activeSession.dispose();
       activeSession = null;
   }

   const errorData = {
       data: {
           status: "session_expired",
           message: "Checkout session expired",
           transaction_id: null,
           id: null
       }
   };
   const $context = objectWithAccessors({
       $chargeData: () => data,
       $state: () => store.getState(),
       $$data: () => errorData
   }, false);


    let baseExpires = 15;
    const getExpiry = SearchParams.get('custom_session_duration') || 15;
    if (!isNaN(getExpiry)) {
        baseExpires = (getExpiry <= 0 || getExpiry > 15) ? 15 : getExpiry;
    }

   // Per-session state. Closure-local and never shared between invocations, so
   // two sessions can never stomp on each other's handles.
   let timerId = null;
   let unsubscribe = null;
   let disposed = false;
   let graceUntil = 0;          // one-shot post-switch grace deadline
   const sessionStart = Date.now();

   // Defensive: the store may be replaced/disposed mid-session.
   function readState() {
       try {
           return $context.$state;
       } catch (_) {
           return null;
       }
   }

   function currentMethod() {
       const state = readState();
       return (state && state.method && state.method.current) || null;
   }

   // Allowed window for the currently-selected method, in milliseconds.
   // A malformed override (0, negative, NaN, non-number) falls back to the
   // baseline rather than producing an instant expiry or a runaway timer.
   function allowedDurationMs() {
       const override = METHOD_SESSION_DURATION[currentMethod()];
       const mins = (typeof override === 'number' && override > 0 && override <= 60) ? override : baseExpires;
       return mins * 60000;
   }

   function dispose() {
       disposed = true;
       if (timerId) {
           clearTimeout(timerId);
           timerId = null;
       }
       if (unsubscribe) {
           try {
               unsubscribe();
           } catch (_) { /* store already torn down */ }
           unsubscribe = null;
       }
   }

   function expire() {
        if (disposed) return;
        dispose();
        if (activeSession === session) activeSession = null;

        try {
            store.dispatch({ type: SET_SESSION_EXPIRED });
        } catch (_) { /* store torn down */ }
    }

   function arm() {
        if (disposed) return;
        if (timerId) {
            clearTimeout(timerId);
            timerId = null;
        }

        const now = Date.now();
        const remaining = allowedDurationMs() - (now - sessionStart);
        // Honour the post-switch grace floor if it is still in effect.
        const effective = Math.max(remaining, graceUntil - now);

        if (effective > 0) {
            try {
                store.dispatch({ type: SET_SESSION_EXPIRY, payload: now + effective });
            } catch (_) { /* store torn down */ }
            timerId = setTimeout(arm, effective);
            return;
        }

        expire();
    }

   // Single absolute anchor: the window measures how long the checkout has been
   // open, NOT how long since the last click. The allowed length tracks the
   // currently-selected method (OPay 30m, otherwise the 15m baseline), so the
   // session is naturally bounded by the largest configured duration and cannot
   // be extended indefinitely by toggling methods. A switch re-arms against the
   // new method (with the grace floor above) but keeps the same start.
   let lastMethod = currentMethod();

   try {
       unsubscribe = store.subscribe(() => {
           if (disposed) return;
           try {
               const method = currentMethod();
               if (method !== lastMethod) {
                   lastMethod = method;
                   graceUntil = Date.now() + SWITCH_GRACE_MS;
                   arm();
               }
           } catch (_) {
               // Store read failed (disposed/replaced mid-session): stop
               // listening so we don't spin on a dead store.
               dispose();
           }
       });
   } catch (_) {
       unsubscribe = null;
   }

   const session = { dispose };
   activeSession = session;

   arm();
}


export default getTimeout;
